import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import CourseLeaderboard from "@/components/CourseLeaderboard";
import { Sparkles, Play, ArrowLeft, MapPin, User, Heart } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CourseLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/courses/by-code/${code}`, { withCredentials: true });
        setCourse(r.data);
      } catch (e) {
        toast.error("Course not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  const toggleLike = async () => {
    if (!course) return;
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/courses/${course.course_id}/like`,
        {},
        { withCredentials: true },
      );
      setCourse({ ...course, likes: r.data.likes, is_liked_by_me: r.data.is_liked_by_me });
    } catch (e) {
      toast.error("Could not like course");
    }
  };

  const startRace = async () => {
    if (!course) return;
    setStarting(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/lobbies`,
        { city_id: course.base_city_id, course_id: course.course_id },
        { withCredentials: true },
      );
      navigate(`/lobbies/${r.data.lobby_id}`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        // Stash the code and route to sign-in
        localStorage.setItem("nah_course_code", code);
        toast.error("Sign in to run this course");
        navigate("/");
      } else {
        toast.error(e?.response?.data?.detail || "Could not start course");
      }
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="px-6 py-20 text-center text-white/60 text-sm">Loading course…</div>
      </AppLayout>
    );
  }
  if (!course) {
    return (
      <AppLayout>
        <div className="px-6 py-20 text-center" data-testid="course-landing-missing">
          <div className="font-heading text-3xl">Course not found</div>
          <div className="text-white/60 text-sm mt-2">
            That link is either wrong, withdrawn, or not published yet.
          </div>
          <button onClick={() => navigate("/courses")} className="btn-primary mt-6">
            Browse public courses
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="px-6 lg:px-12 py-10 max-w-[1100px] mx-auto" data-testid="course-landing-page">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white/60 hover:text-white text-sm mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className="label-kicker flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-[#FFCC00]" /> Custom heist invite
            </div>
            <h1 className="font-heading text-5xl lg:text-6xl mt-2">{course.name}</h1>
            <div className="mt-4 flex items-center gap-4 text-sm text-white/60">
              <span className="flex items-center gap-1">
                <User className="w-3.5 h-3.5" /> by{" "}
                <b className="text-white">{course.owner_name}</b>
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                <b className="text-white">{course.base_city_name}</b>
              </span>
              <span>{course.items?.length || 0} artifacts · {course.play_count} plays</span>
              <button
                data-testid="course-landing-like"
                onClick={toggleLike}
                className={`flex items-center gap-1 transition-colors ${
                  course.is_liked_by_me ? "text-[#FF3B30]" : "text-white/40 hover:text-[#FF3B30]"
                }`}
                title={course.is_liked_by_me ? "Unlike" : "Like"}
              >
                <Heart
                  className="w-3.5 h-3.5"
                  fill={course.is_liked_by_me ? "currentColor" : "none"}
                />
                <span className="font-mono">{course.likes ?? 0}</span>
              </button>
            </div>
            {course.description && (
              <p className="mt-6 text-white/80 text-base max-w-xl">{course.description}</p>
            )}
            <button
              onClick={startRace}
              disabled={starting}
              className="btn-primary mt-8 flex items-center gap-2 disabled:opacity-50"
              data-testid="course-landing-start"
            >
              <Play className="w-4 h-4" />
              {starting ? "Loading lobby…" : "Start the heist"}
            </button>
          </div>

          {/* Items preview */}
          <div className="surface-solid p-5">
            <div className="label-kicker">The 5 targets</div>
            <div className="space-y-2 mt-3">
              {course.items.map((it, idx) => (
                <div
                  key={it.item_id}
                  className="flex items-center gap-3 p-2 border border-white/10"
                  data-testid={`course-landing-item-${it.item_id}`}
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-black border border-white/10 shrink-0">
                    {it.icon_url ? (
                      <img
                        src={`${BACKEND_URL}${it.icon_url}`}
                        alt={it.name}
                        className="w-full h-full object-contain"
                        style={{ imageRendering: "pixelated" }}
                      />
                    ) : (
                      <Sparkles className="w-5 h-5 text-[#FFCC00]" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-heading truncate">
                      {idx + 1}. {it.name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-5 border-t border-white/10">
              <CourseLeaderboard courseId={course.course_id} compact />
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
